import { Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription } from 'rxjs';
import { JJamesTimelineChartComponent, TimelineParkingEvent, toDateInputValue } from 'shared-lib';
import { ParkerPoolCapUsageDto } from '../../../api/gen';
import { ParkerPoolCapStore } from './parker-pool-cap.store';

/**
 * One parker's cars-inside cap on the plate page: who owns it, every right counting against it,
 * and the occupancy over a window — pool-full stays drawn apart, as on the organization page.
 *
 * A right whose owner is not the cap's owner means two people share one slot, and whoever enters
 * second pays short-term (Schobesberger / Holzinger, Oct 2026). That is flagged rather than left
 * to be read off the chart.
 */
@Component({
  selector: 'app-parker-pool-cap',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatProgressSpinnerModule, JJamesTimelineChartComponent],
  templateUrl: './parker-pool-cap.component.html'
})
export class ParkerPoolCapComponent {
  private readonly store = inject(ParkerPoolCapStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly poolParkingConfigId = input.required<string>();

  readonly usage = signal<ParkerPoolCapUsageDto | null>(null);
  readonly loadingUsage = signal(true);

  /** Stays are fetched for the last 30 days; the chart is drawn from the first stay to the last. */
  private readonly from = toDateInputValue(daysAgo(29));
  private readonly to = toDateInputValue(new Date());

  readonly events = signal<TimelineParkingEvent[]>([]);
  readonly truncated = signal(false);
  readonly loadingTimeline = signal(false);
  readonly timelineFailed = signal(false);

  /** Rights on this cap held by someone other than its owner. */
  readonly foreignRights = computed(() => (this.usage()?.rights ?? []).filter(r => !r.ownedByPoolOwner));
  readonly overflowCount = computed(() => this.events().filter(e => e.isOverflow).length);
  readonly windowStart = computed(() => new Date(Math.min(...this.events().map(e => new Date(e.startedAt).getTime()))));
  // A car still inside runs the window up to now.
  readonly windowEnd = computed(() => new Date(Math.max(...this.events().map(e => e.endedAt ? new Date(e.endedAt).getTime() : Date.now()))));

  private timelineSub?: Subscription;

  constructor() {
    effect(() => {
      const id = this.poolParkingConfigId();
      untracked(() => {
        this.loadingUsage.set(true);
        this.store.usage(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(usage => {
          this.usage.set(usage);
          this.loadingUsage.set(false);
        });
        this.loadTimeline(id);
      });
    });
  }

  private loadTimeline(id: string): void {
    // A newer cap replaces the one in flight rather than racing it.
    this.timelineSub?.unsubscribe();
    this.loadingTimeline.set(true);
    this.timelineFailed.set(false);
    this.timelineSub = this.store.timeline(id, this.from, this.to)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.events.set(result?.events ?? []);
        this.truncated.set(result?.truncated ?? false);
        this.timelineFailed.set(result === null);
        this.loadingTimeline.set(false);
      });
  }
}

function daysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
}
